import { useEffect, useState } from 'react'
import {
  InteractionRequiredAuthError,
  InteractionStatus,
  IPublicClientApplication,
  RedirectRequest,
  SilentRequest,
} from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { QueryKey } from '@tanstack/react-query'

/**
 * Internal hook to encapsulate the logic for getting an access token.
 * @param msalRequest
 * @returns
 */
export const useGetAuthToken = (
  msalRequest: SilentRequest | RedirectRequest,
  queryKey: QueryKey
) => {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [accessToken, setAccessToken] = useState<string | null>(null)

  useEffect(() => {
    if (isAuthenticated && inProgress === InteractionStatus.None) {
      getAuthTokenPromise(instance, msalRequest).then((token) => {
        setAccessToken(token)
      })
    }
  }, [inProgress, isAuthenticated, queryKey])

  return accessToken
}

/**
 * getAuthTokenPromise() will first try to get the token from the cache (localStorage) using acquireTokenSilent
 * then redirect to the login page if the token is not found or expired.
 *
 * @param msalRequest
 * @returns Promise<string> resolving to the access token
 */
export const getAuthTokenPromise = (
  instance: IPublicClientApplication,
  msalRequest: SilentRequest | RedirectRequest
): Promise<string | null> => {
  return instance
    .acquireTokenSilent(msalRequest)
    .then((response) => response.accessToken)
    .catch((error) => {
      if (error instanceof InteractionRequiredAuthError) {
        // fallback to interaction when silent call fails
        instance.acquireTokenRedirect(msalRequest)
      }
      return null
    })
}
